import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from './app/App';
import { createI18n } from './app/providers';
import { api } from './shared/api';
import { customDomainHost, setServedShowcase } from './shared/config/customDomain';
import { env } from './shared/config/env';
import { disabledLocaleRedirect, localeFromPath } from './shared/config/locales';
import './styles/index.css';

// A switched-off locale ("/ru/..." while only uk is served) goes to the same
// page in uk before anything renders. It cannot be a router <Navigate>: the
// router's basename is the locale prefix, and it cannot leave its own basename.
const redirect = disabledLocaleRedirect(window.location.pathname);
if (redirect)
  window.location.replace(`${redirect}${window.location.search}${window.location.hash}`);

// The URL decides the language: "/en/..." renders English under the "/en"
// basename, so every in-app link stays inside the current locale.
const { locale, basename } = localeFromPath(window.location.pathname);
document.documentElement.lang = locale;

const root = document.getElementById('root')!;
// On a Studio master's own domain the page is their showcase, not whatever
// markup the web server prerendered for "/", so it is never hydrated.
const customHost = customDomainHost(window.location.hostname);
// A prerendered page marks which URL it was built for. When this document is
// a fallback served for another URL, render from scratch instead of hydrating
// markup that belongs to a different page.
const prerendered =
  !customHost && root.dataset.route === window.location.pathname && window.__KRASAHUB__;
const { i18n } = createI18n(locale, prerendered ? window.__KRASAHUB__?.resources : undefined);

const app = (
  <StrictMode>
    <BrowserRouter basename={basename || undefined}>
      <App i18n={i18n} />
    </BrowserRouter>
  </StrictMode>
);

const mount = async () => {
  if (customHost) {
    try {
      setServedShowcase((await api.get<{ slug: string }>(`/domains/${customHost}`)).slug);
    } catch {
      // Not a served domain (unverified, unpublished, off the plan): the
      // ordinary site renders, and the web server will not have issued TLS.
    }
  }
  if (prerendered) {
    hydrateRoot(root, app);
    return;
  }
  root.textContent = '';
  createRoot(root).render(app);
};

// The worker has to be listening before the first request goes out, otherwise
// the page renders an error and only recovers on refetch. Loaded dynamically
// so msw never reaches a production bundle.
if (env.enableMocks) {
  void import('./mocks/browser').then(({ startMockApi }) => startMockApi().then(mount));
} else {
  void mount();
}
